import React, { useState, useEffect, useRef } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { useTenant } from '../contexts/TenantContext';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { ShieldCheck, ShieldAlert, Database, Server, Users, Activity, Settings, HardDrive, AlertTriangle, MessageSquare, Monitor, CreditCard, ChevronLeft, ChevronRight, Newspaper } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAdminSync } from '../hooks/useAdmin';
import { useLedgerTransactions } from '../hooks/useLedger';
import { Spinner } from '../components/ui/Spinner';
import { apiWorker } from '../api/worker';
import { cn } from '../lib/utils';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { AccessGate } from '../components/ui/AccessGate';

import { AdminUsersManager } from '../components/system-admin/AdminUsersManager';
import { AdminNodesManager } from '../components/system-admin/AdminNodesManager';
import { AdminGlobalBroadcasts } from '../components/system-admin/AdminGlobalBroadcasts';
import { AdminBroadcastModal } from '../components/system-admin/AdminBroadcastModal';
import { AdminMaintenanceManager } from '../components/system-admin/AdminMaintenanceManager';
import { AdminContactInquiries } from '../components/system-admin/AdminContactInquiries';
import { AdminModerationManager } from '../components/system-admin/AdminModerationManager';
import { AdminTicketsManager } from '../components/system-admin/AdminTicketsManager';
import { AdminAuditManager } from '../components/system-admin/AdminAuditManager';
import { AdminNewsroomManager } from '../components/system-admin/AdminNewsroomManager';

interface SystemAdminPageProps {
  initialTab?: string;
}

export const SystemAdminPage: React.FC<SystemAdminPageProps> = ({ initialTab }) => {
  const { isSystemAdmin, isSuperAdmin, isOperationsAdmin, isSupportAdmin, role } = usePermissions();
  const { node } = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');

  const [activeTab, setActiveTab] = useState<string>(() => initialTab || urlTab || 'dashboard');

  const [prevTabState, setPrevTabState] = useState({ initialTab, urlTab });
  if (initialTab !== prevTabState.initialTab || urlTab !== prevTabState.urlTab) {
    setPrevTabState({ initialTab, urlTab });
    const target = initialTab || urlTab || 'dashboard';
    if (target !== activeTab) {
      setActiveTab(target);
    }
  }

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams(tabId === 'dashboard' ? {} : { tab: tabId }, { replace: true });
  };

  const tabScrollRef = useRef<HTMLDivElement>(null);
  const activeTabRef = useRef<HTMLButtonElement>(null);

  const scrollTabs = (direction: 'left' | 'right') => {
    if (tabScrollRef.current) {
      tabScrollRef.current.scrollBy({
        left: direction === 'left' ? -220 : 220,
        behavior: 'smooth'
      });
    }
  };

  useEffect(() => {
    if (activeTabRef.current) {
      activeTabRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [activeTab]);

  const [broadcastOpen, setBroadcastOpen] = useState(false);

  const syncMutation = useAdminSync();

  // Maintenance state and mutation
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  useEffect(() => {
    apiWorker.get('/api/system/status')
      .then((res) => {
        if (res.data?.maintenance !== undefined) {
          setMaintenanceMode(res.data.maintenance === true || res.data.maintenance === 'true' || res.data.status === 'MAINTENANCE');
        }
      })
      .catch(() => {});
  }, []);
  const maintenanceMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      await apiWorker.post('/api/admin/platform/maintenance', { enabled });
    },
    onSuccess: (_, enabled) => {
      setMaintenanceMode(enabled);
      toast.success(enabled ? 'Platform Locked (Maintenance Mode)' : 'Platform Unlocked');
    },
    onError: () => toast.error('Failed to update maintenance mode')
  });

  const handleHealthCheck = async () => {
    try {
      await apiWorker.get('/api/health');
      toast.success('Health checks passed successfully across all regions.');
    } catch {
      toast.error('Health checks failed.');
    }
  };

  // Global Ledger Overrides States & Hook
  const [ledgerPage, setLedgerPage] = useState(0);
  const [targetNodeId, setTargetNodeId] = useState(() => {
    if (!node || node.id === 'platform-root') {
      return 'all';
    }
    return node.id;
  });

  const { data: ledgerData, isLoading: isLoadingLedger } = useLedgerTransactions(targetNodeId, true, ledgerPage, 10);

  // Global Ledger Overrides Mutations
  const markPaidMutation = useMutation({
    mutationFn: async ({ txId, explanation }: { txId: string, explanation: string }) => {
      await apiWorker.patch(`/api/admin/billing/transactions/${txId}/manual-status`, { status: 'PAID', reason: explanation });
    },
    onSuccess: () => {
      toast.success('Transaction status overridden to PAID');
      queryClient.invalidateQueries({ queryKey: ['ledger-transactions'] });
    },
    onError: () => toast.error('Failed to override transaction status')
  });

  const waiveMutation = useMutation({
    mutationFn: async ({ nodeId, userId }: { nodeId: string, userId: string }) => {
      await apiWorker.post(`/api/admin/nodes/${nodeId}/members/${userId}/waive-dues`);
    },
    onSuccess: () => {
      toast.success('Dues marked as Waived for current cycle');
      queryClient.invalidateQueries({ queryKey: ['ledger-transactions'] });
    },
    onError: () => toast.error('Failed to waive dues')
  });

  const refundMutation = useMutation({
    mutationFn: async ({ txId, amount }: { txId: string, amount: number }) => {
      await apiWorker.post(`/api/admin/billing/refund`, { paymentIntentId: txId, amount });
    },
    onSuccess: () => {
      toast.success('Refund processed successfully');
      queryClient.invalidateQueries({ queryKey: ['ledger-transactions'] });
    },
    onError: () => toast.error('Failed to process refund')
  });

  // Lineage Split Simulator State & Mutation
  const [showSim, setShowSim] = useState(false);
  const [simNodeId, setSimNodeId] = useState('node-1');
  const [simAmount, setSimAmount] = useState('100');
  const [simResult, setSimResult] = useState<any>(null);

  const simulateMutation = useMutation({
    mutationFn: async () => {
      const res = await apiWorker.post(`/api/admin/lineage/${simNodeId}/simulate-split`, { amount: parseFloat(simAmount) });
      return res.data;
    },
    onSuccess: (data) => {
      setSimResult(data);
      toast.success('Fee split simulated successfully');
    },
    onError: () => toast.error('Failed to simulate fee split')
  });

  // Telemetry Monitor Query
  const { data: deliveryStats, isLoading: isLoadingDelivery } = useQuery({
    queryKey: ['admin', 'monitoring', 'delivery'],
    queryFn: async () => {
      const res = await apiWorker.get('/api/admin/monitoring/delivery');
      return res.data;
    },
    enabled: activeTab === 'telemetry'
  });

  // IP Blacklist State, Query & Mutations
  const [blacklistIp, setBlacklistIp] = useState('');
  const { data: blacklistedIps = [], isLoading: isLoadingBlacklist } = useQuery({
    queryKey: ['admin', 'blacklist'],
    queryFn: async () => {
      const res = await apiWorker.get('/api/admin/audit/blacklist/ip');
      return Array.isArray(res.data) ? res.data : [];
    }
  });

  const blacklistMutation = useMutation({
    mutationFn: async (ip: string) => {
      await apiWorker.post('/api/admin/audit/blacklist/ip', { ipAddress: ip });
    },
    onSuccess: () => {
      toast.success(`IP Address ${blacklistIp} has been blacklisted`);
      setBlacklistIp('');
      queryClient.invalidateQueries({ queryKey: ['admin', 'blacklist'] });
    },
    onError: () => toast.error('Failed to blacklist IP address')
  });

  const unblacklistMutation = useMutation({
    mutationFn: async (ip: string) => {
      await apiWorker.delete(`/api/admin/audit/blacklist/ip/${encodeURIComponent(ip)}`);
    },
    onSuccess: (_, ip) => {
      toast.success(`IP Address ${ip} removed from blacklist`);
      queryClient.invalidateQueries({ queryKey: ['admin', 'blacklist'] });
    },
    onError: () => toast.error('Failed to remove IP from blacklist')
  });

  if (!isSystemAdmin && !isSuperAdmin) {
    return (
      <AccessGate
        reason="platform-admin"
        role={role}
        requirement="You need System Administrator clearance to view this dashboard."
        onBack={() => navigate('/dashboard')}
      />
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'nodes', label: 'Organizations', roles: ['SUPER_ADMIN', 'SYSTEM_ADMIN', 'OPERATIONS_ADMIN'] },
    { id: 'users', label: 'Users', roles: ['SUPER_ADMIN', 'SYSTEM_ADMIN', 'SUPPORT_ADMIN', 'OPERATIONS_ADMIN'] },
    { id: 'moderation', label: 'Moderation', roles: ['SUPER_ADMIN', 'SYSTEM_ADMIN', 'SUPPORT_ADMIN'] },
    { id: 'tickets', label: 'Support Tickets', roles: ['SUPER_ADMIN', 'SYSTEM_ADMIN', 'SUPPORT_ADMIN'] },
    { id: 'inquiries', label: 'Inquiries', roles: ['SUPER_ADMIN', 'SYSTEM_ADMIN', 'SUPPORT_ADMIN', 'OPERATIONS_ADMIN'] },
    { id: 'audit', label: 'Audit Logs', roles: ['SUPER_ADMIN', 'SYSTEM_ADMIN', 'SUPPORT_ADMIN'] },
    { id: 'ledger', label: 'Global Ledger', roles: ['SUPER_ADMIN', 'SYSTEM_ADMIN', 'OPERATIONS_ADMIN'] },
    { id: 'maintenance', label: 'Maintenance & Storage', roles: ['SUPER_ADMIN', 'SYSTEM_ADMIN', 'OPERATIONS_ADMIN'] },
    { id: 'telemetry', label: 'Telemetry & Logs', roles: ['SUPER_ADMIN', 'SYSTEM_ADMIN', 'SUPPORT_ADMIN', 'OPERATIONS_ADMIN'] },
    { id: 'config', label: 'Platform Config', roles: ['SUPER_ADMIN', 'SYSTEM_ADMIN'] },
    { id: 'newsroom', label: 'Newsroom', roles: ['SUPER_ADMIN', 'SYSTEM_ADMIN', 'SUPPORT_ADMIN'] },
  ];

  const roleName = isSuperAdmin ? 'SUPER ADMIN' : isOperationsAdmin ? 'OPERATIONS ADMIN' : 'SUPPORT ADMIN';

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b pb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-display text-3xl text-gray-900 dark:text-white">System Administration</h1>
            <Badge variant="red" className="uppercase font-mono tracking-widest text-[10px]">{roleName}</Badge>
          </div>
          <p className="text-gray-500">Platform-wide oversight and advanced backend controls.</p>
        </div>
        <div className="flex gap-2">
          {isSuperAdmin && (
            <Button 
              variant="secondary" 
              className={cn("gap-2 border-red-200", maintenanceMode ? "bg-red-600 text-white hover:bg-red-700 border-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-red-600/20" : "bg-red-50 text-red-600 hover:bg-red-100")} 
              onClick={() => maintenanceMutation.mutate(!maintenanceMode)}
              disabled={maintenanceMutation.isPending}
            >
              <AlertTriangle size={16} /> {maintenanceMode ? 'Unlock Platform' : 'Lock Platform'}
            </Button>
          )}
          <Button variant="secondary" className="gap-2" onClick={handleHealthCheck}>
            <Server size={16} /> Check Health
          </Button>
          {(isSuperAdmin || isOperationsAdmin) && (
            <Button variant="primary" className="gap-2 bg-blue-600 hover:bg-blue-700 border-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-blue-600/20 text-white" isLoading={syncMutation.isPending} onClick={() => syncMutation.mutate()}>
              <Database size={16} /> Deep Sync
            </Button>
          )}
        </div>
      </header>

      <div className="relative border-b-2 border-black dark:border-white">
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => scrollTabs('left')}
            aria-label="Scroll tabs left"
            className="p-2 text-black dark:text-white border-r-2 border-black dark:border-white hover:bg-gray-100 dark:hover:bg-slate-800 transition shrink-0 flex items-center justify-center cursor-pointer"
          >
            <ChevronLeft size={16} />
          </button>

          <div
            ref={tabScrollRef}
            className="flex gap-1.5 sm:gap-2 overflow-x-auto scroll-smooth scrollbar-none pb-1 px-1 flex-1 items-center"
          >
            {tabs
              .filter(t => !t.roles || isSuperAdmin || (isOperationsAdmin && t.roles.includes('OPERATIONS_ADMIN')) || (isSupportAdmin && t.roles.includes('SUPPORT_ADMIN')))
              .map(tab => (
                <button
                  key={tab.id}
                  ref={tab.id === activeTab ? activeTabRef : null}
                  onClick={() => handleTabChange(tab.id)}
                  className={cn(
                    "px-3.5 py-2 font-mono text-xs uppercase tracking-wider transition-all whitespace-nowrap shrink-0 border-2 rounded-none font-bold",
                    activeTab === tab.id
                      ? "border-black dark:border-white bg-black text-white dark:bg-white dark:text-black shadow-[2px_2px_0px_#0044FF]"
                      : "border-transparent text-gray-600 dark:text-gray-400 hover:border-black/30 dark:hover:border-white/30 hover:text-black dark:hover:text-white"
                  )}
                >
                  {tab.label}
                </button>
              ))}
          </div>

          <button
            type="button"
            onClick={() => scrollTabs('right')}
            aria-label="Scroll tabs right"
            className="p-2 text-black dark:text-white border-l-2 border-black dark:border-white hover:bg-gray-100 dark:hover:bg-slate-800 transition shrink-0 flex items-center justify-center cursor-pointer"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {activeTab === 'dashboard' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(isSuperAdmin || isOperationsAdmin) && (
              <Card className="flex flex-col items-center justify-center p-8 text-center border-t-4 border-t-blue-500 cursor-pointer hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] transition bg-white dark:bg-slate-900 rounded-none" onClick={() => setActiveTab('nodes')}>
                <div className="w-12 h-12 rounded-none bg-blue-50 text-blue-600 flex items-center justify-center mb-4">
                  <Server size={24} />
                </div>
                <h3 className="text-xl font-display text-gray-900 mb-1 dark:text-white">Manage Organizations</h3>
                <p className="text-xs text-gray-500 mb-4">View global organizations and override tiers.</p>
              </Card>
            )}

            <Card className="flex flex-col items-center justify-center p-8 text-center border-t-4 border-t-purple-500 cursor-pointer hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] transition bg-white dark:bg-slate-900 rounded-none" onClick={() => setActiveTab('users')}>
              <div className="w-12 h-12 rounded-none bg-purple-50 text-purple-600 flex items-center justify-center mb-4">
                <Users size={24} />
              </div>
              <h3 className="text-xl font-display text-gray-900 mb-1 dark:text-white">Manage Users</h3>
              <p className="text-xs text-gray-500 mb-4">Investigate accounts and trigger support tools.</p>
            </Card>

            {(isSuperAdmin || isSupportAdmin || isSystemAdmin) && (
              <Card className="flex flex-col items-center justify-center p-8 text-center border-t-4 border-t-red-600 cursor-pointer hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] transition bg-white dark:bg-slate-900 rounded-none" onClick={() => setActiveTab('moderation')}>
                <div className="w-12 h-12 rounded-none bg-red-50 text-red-600 flex items-center justify-center mb-4">
                  <ShieldAlert size={24} />
                </div>
                <h3 className="text-xl font-display text-gray-900 mb-1 dark:text-white">Moderation Queue</h3>
                <p className="text-xs text-gray-500 mb-4">Review flagged content, quarantine assets, and mute users.</p>
              </Card>
            )}

            <Card className="flex flex-col items-center justify-center p-8 text-center border-t-4 border-t-green-500 cursor-pointer hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] transition bg-white dark:bg-slate-900 rounded-none" onClick={() => setActiveTab('telemetry')}>
              <div className="w-12 h-12 rounded-none bg-green-50 text-green-600 flex items-center justify-center mb-4">
                <Activity size={24} />
              </div>
              <h3 className="text-xl font-display text-gray-900 mb-1 dark:text-white">Platform Stats</h3>
              <p className="text-xs text-gray-500 mb-4">View global metrics and core service health.</p>
            </Card>

            {isSuperAdmin && (
              <Card className="flex flex-col items-center justify-center p-8 text-center border-t-4 border-t-yellow-500 cursor-pointer hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] transition bg-white dark:bg-slate-900 rounded-none" onClick={() => setBroadcastOpen(true)}>
                <div className="w-12 h-12 rounded-none bg-yellow-50 text-yellow-600 flex items-center justify-center mb-4">
                  <MessageSquare size={24} />
                </div>
                <h3 className="text-xl font-display text-gray-900 mb-1 dark:text-white">Global Broadcast</h3>
                <p className="text-xs text-gray-500 mb-4">Send platform-wide announcements to all users.</p>
              </Card>
            )}

            {(isSuperAdmin || isOperationsAdmin) && (
              <Card className="flex flex-col items-center justify-center p-8 text-center border-t-4 border-t-red-500 cursor-pointer hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] transition bg-white dark:bg-slate-900 rounded-none" onClick={() => setActiveTab('maintenance')}>
                <div className="w-12 h-12 rounded-none bg-red-50 text-red-600 flex items-center justify-center mb-4">
                  <Database size={24} />
                </div>
                <h3 className="text-xl font-display text-gray-900 mb-1 dark:text-white">Maintenance & Storage</h3>
                <p className="text-xs text-gray-500 mb-4">Clean orphaned records and purge unreferenced vault files.</p>
              </Card>
            )}

            {(isSuperAdmin || isSupportAdmin) && (
              <Card className="flex flex-col items-center justify-center p-8 text-center border-t-4 border-t-blue-400 cursor-pointer hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] transition bg-white dark:bg-slate-900 rounded-none" onClick={() => handleTabChange('newsroom')}>
                <div className="w-12 h-12 rounded-none bg-blue-50 text-blue-500 flex items-center justify-center mb-4">
                  <Newspaper size={24} />
                </div>
                <h3 className="text-xl font-display text-gray-900 mb-1 dark:text-white">Newsroom</h3>
                <p className="text-xs text-gray-500 mb-4">Create and publish public newsroom articles.</p>
              </Card>
            )}

            {(isSuperAdmin || isSupportAdmin) && (
              <Card className="flex flex-col items-center justify-center p-8 text-center border-t-4 border-t-amber-500 cursor-pointer hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] transition bg-white dark:bg-slate-900 rounded-none" onClick={() => handleTabChange('tickets')}>
                <div className="w-12 h-12 rounded-none bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
                  <MessageSquare size={24} />
                </div>
                <h3 className="text-xl font-display text-gray-900 mb-1 dark:text-white">Support Tickets</h3>
                <p className="text-xs text-gray-500 mb-4">Review escalated issues and resolve tickets.</p>
              </Card>
            )}

            {(isSuperAdmin || isSupportAdmin) && (
              <Card className="flex flex-col items-center justify-center p-8 text-center border-t-4 border-t-indigo-500 cursor-pointer hover:shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] transition bg-white dark:bg-slate-900 rounded-none" onClick={() => handleTabChange('audit')}>
                <div className="w-12 h-12 rounded-none bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4">
                  <ShieldCheck size={24} />
                </div>
                <h3 className="text-xl font-display text-gray-900 mb-1 dark:text-white">Audit Logs</h3>
                <p className="text-xs text-gray-500 mb-4">Investigate system events and export CSV logs.</p>
              </Card>
            )}
          </div>

          {(isSuperAdmin || isOperationsAdmin) && (
            <div className="mt-8">
              <AdminGlobalBroadcasts />
            </div>
          )}
        </>
      )}

      {activeTab === 'ledger' && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Financial Overrides</h2>
              <p className="text-xs text-gray-500">View and override billing transactions for the platform.</p>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-80">
              <label className="text-xs font-semibold text-gray-500 whitespace-nowrap">Filter Org ID:</label>
              <input 
                type="text" 
                placeholder="Enter Org ID or 'all'" 
                className="w-full px-3 py-1.5 text-xs border-2 border-black dark:border-white rounded-none bg-white dark:bg-slate-900 border-black dark:border-white dark:border-white focus:ring-2 focus:ring-blue-500 outline-none transition"
                value={targetNodeId}
                onChange={e => {
                  setTargetNodeId(e.target.value);
                  setLedgerPage(0);
                }}
              />
            </div>
          </div>

          <Card className="p-6 bg-white dark:bg-slate-900 rounded-none">
            {isLoadingLedger ? (
              <div className="flex justify-center py-8"><Spinner size="md" /></div>
            ) : (() => {
              const ledgerItems = Array.isArray(ledgerData?.content) 
                ? ledgerData.content 
                : (Array.isArray(ledgerData) ? ledgerData : []);
              if (ledgerItems.length === 0) {
                return (
                  <p className="text-center text-sm text-gray-500 py-8">
                    No transactions found. Type a specific Org ID above to view and manage its overrides.
                  </p>
                );
              }
              return (
                <div className="space-y-6">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="border-b text-gray-500">
                        <th className="pb-2">Transaction ID</th>
                        <th className="pb-2">Description</th>
                        <th className="pb-2">Amount</th>
                        <th className="pb-2">Status</th>
                        <th className="pb-2 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerItems.map((tx: any) => (
                      <tr key={tx.id} className="border-b dark:border-white">
                        <td className="py-3 font-mono text-xs text-gray-900 dark:text-white">{tx.id}</td>
                        <td className="py-3 text-gray-600 dark:text-gray-400">{tx.description}</td>
                        <td className="py-3 font-semibold text-gray-900 dark:text-white">${tx.amount.toFixed(2)}</td>
                        <td className="py-3">
                          <Badge 
                            variant={
                              tx.status === 'PAID' ? 'green' : 
                              tx.status === 'PENDING' ? 'amber' : 
                              tx.status === 'REFUNDED' ? 'blue' : 'red'
                            }
                          >
                            {tx.status}
                          </Badge>
                        </td>
                        <td className="py-3 text-right space-x-2">
                          {tx.status === 'PENDING' && (
                            <>
                              <Button 
                                variant="secondary" 
                                size="sm" 
                                onClick={() => {
                                  const exp = window.prompt("Reason for manual override to PAID status:");
                                  if (exp) {
                                    markPaidMutation.mutate({ txId: tx.id, explanation: exp });
                                  }
                                }}
                                disabled={markPaidMutation.isPending}
                              >
                                Mark Paid
                              </Button>
                              <Button 
                                variant="secondary" 
                                size="sm" 
                                onClick={() => waiveMutation.mutate({ nodeId: tx.nodeId, userId: tx.userId })}
                                disabled={waiveMutation.isPending}
                              >
                                Waive Dues
                              </Button>
                            </>
                          )}
                          {tx.status === 'PAID' && (
                            <Button 
                              variant="secondary" 
                              size="sm" 
                              className="text-red-600 hover:bg-red-50 border-red-100 dark:hover:bg-red-950/20" 
                              onClick={() => {
                                const amt = window.prompt(`Refund Amount (leave empty for full $${tx.amount.toFixed(2)}):`);
                                if (amt !== null) {
                                  refundMutation.mutate({ txId: tx.id, amount: amt ? parseFloat(amt) : tx.amount });
                                }
                              }}
                              disabled={refundMutation.isPending}
                            >
                              Refund
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {ledgerData && ledgerData.totalPages > 1 && (
                  <div className="flex items-center justify-between border-t dark:border-white pt-4">
                    <span className="text-xs text-gray-500">
                      Page <span className="font-semibold text-gray-700 dark:text-gray-300">{ledgerPage + 1}</span> of <span className="font-semibold text-gray-700 dark:text-gray-300">{ledgerData.totalPages}</span>
                    </span>
                    <div className="flex gap-2">
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        disabled={ledgerPage === 0} 
                        onClick={() => setLedgerPage(p => Math.max(0, p - 1))}
                        className="gap-1.5 px-3"
                      >
                        <ChevronLeft size={14} /> Prev
                      </Button>
                      <Button 
                        variant="secondary" 
                        size="sm" 
                        disabled={ledgerPage >= ledgerData.totalPages - 1} 
                        onClick={() => setLedgerPage(p => p + 1)}
                        className="gap-1.5 px-3"
                      >
                        Next <ChevronRight size={14} />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
          </Card>

          <Card className="p-6 border-l-4 border-blue-500 bg-white dark:bg-slate-900 rounded-none">
            <h3 className="font-bold flex items-center gap-2 mb-2"><CreditCard size={18} /> Network Lineage & Fee Simulator</h3>
            <p className="text-sm text-gray-500 mb-4">Analyze and simulate complex fee splits across the node lineage.</p>
            {showSim ? (
              <div className="space-y-4 max-w-md bg-white dark:bg-slate-950 p-4 rounded-none border">
                <div>
                  <label className="block text-xs font-bold text-gray-400 mb-1">Org ID</label>
                  <input type="text" className="w-full text-xs p-2 border-2 border-black dark:border-white rounded bg-white dark:bg-slate-900" value={simNodeId} onChange={e => setSimNodeId(e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 mb-1">Transaction Amount ($)</label>
                  <input type="number" className="w-full text-xs p-2 border-2 border-black dark:border-white rounded bg-white dark:bg-slate-900" value={simAmount} onChange={e => setSimAmount(e.target.value)} />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="secondary" onClick={() => setShowSim(false)}>Close</Button>
                  <Button size="sm" onClick={() => simulateMutation.mutate()} disabled={simulateMutation.isPending}>Simulate</Button>
                </div>
                {Array.isArray(simResult) && (
                  <div className="mt-3 p-3 bg-white dark:bg-slate-900 border-2 border-black dark:border-white rounded-none space-y-2">
                    <h4 className="font-bold text-xs">Simulated Splits:</h4>
                    {simResult.map((res: any, idx: number) => (
                      <div key={idx} className="flex justify-between text-xs font-mono">
                        <span>{res.nodeName}:</span>
                        <span>${res.amount.toFixed(2)} ({res.percentage}%)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Button variant="secondary" onClick={() => setShowSim(true)}>Launch Simulator</Button>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'telemetry' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="p-6 bg-white dark:bg-slate-900 rounded-none">
            <h3 className="font-bold mb-4 flex items-center gap-2"><Monitor size={18} /> Service Delivery Gateway</h3>
            {isLoadingDelivery ? (
              <div className="flex justify-center"><Spinner size="md"/></div>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1"><span>Twilio SMS</span><span className="text-green-600 font-semibold">{deliveryStats?.smsSuccessRate != null ? `${deliveryStats.smsSuccessRate}% Success` : 'N/A'}</span></div>
                  <div className="w-full bg-gray-200 dark:bg-slate-800 rounded-none h-2">
                    <div className="bg-green-500 h-2 rounded-none" style={{ width: `${deliveryStats?.smsSuccessRate ?? 0}%` }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1"><span>SendGrid Email</span><span className="text-green-600 font-semibold">{deliveryStats?.emailSuccessRate != null ? `${deliveryStats.emailSuccessRate}% Success` : 'N/A'}</span></div>
                  <div className="w-full bg-gray-200 dark:bg-slate-800 rounded-none h-2">
                    <div className="bg-green-500 h-2 rounded-none" style={{ width: `${deliveryStats?.emailSuccessRate ?? 0}%` }}></div>
                  </div>
                </div>
              </div>
            )}
          </Card>

          <Card className="p-6 bg-white dark:bg-slate-900 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold flex items-center gap-2"><ShieldAlert size={18} /> Active IP Blacklist</h3>
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={() => navigate('/system-admin/audit')}
                className="text-xs"
              >
                Audit Trail &rarr;
              </Button>
            </div>

            <div className="space-y-2 mb-4 max-h-36 overflow-y-auto">
              {isLoadingBlacklist ? (
                <div className="py-2 text-xs text-gray-500 font-mono">Loading blacklist...</div>
              ) : blacklistedIps.length === 0 ? (
                <div className="text-xs text-gray-400 font-mono py-2">No active IP bans.</div>
              ) : (
                blacklistedIps.map((ip: string) => (
                  <div key={ip} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-black border border-gray-200 dark:border-gray-800">
                    <span className="font-mono text-xs text-red-500 font-bold">{ip}</span>
                    <button 
                      onClick={() => unblacklistMutation.mutate(ip)} 
                      className="text-[10px] uppercase font-bold text-gray-400 hover:text-red-500 transition-colors"
                      disabled={unblacklistMutation.isPending}
                    >
                      Unban
                    </button>
                  </div>
                ))
              )}
            </div>
            
            <div className="flex gap-2 pt-3 border-t">
              <input 
                type="text" 
                placeholder="Blacklist IP Address (e.g. 192.168.1.1)" 
                className="text-xs border-2 border-black dark:border-white rounded-none p-2 flex-1 bg-white dark:bg-slate-900" 
                value={blacklistIp}
                onChange={e => setBlacklistIp(e.target.value)}
              />
              <Button 
                variant="secondary" 
                className="text-xs text-red-600 border-red-200 bg-red-50 hover:bg-red-100 rounded-none font-bold" 
                onClick={() => {
                  if (!blacklistIp.trim()) return toast.error('IP address required');
                  blacklistMutation.mutate(blacklistIp.trim());
                }}
                disabled={blacklistMutation.isPending}
              >
                Blacklist
              </Button>
            </div>
          </Card>

          {(isSuperAdmin || isOperationsAdmin) && (
            <Card className="p-6 md:col-span-2 bg-white dark:bg-slate-900 rounded-none border-2 border-black dark:border-white shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="font-bold flex items-center gap-2 mb-1"><HardDrive size={18} /> Storage & Database Hygiene Console</h3>
                  <p className="text-sm text-gray-500">Scan & purge unreferenced database entities and reclaim orphaned vault file storage.</p>
                </div>
                <Button variant="primary" className="bg-[#0044FF] hover:bg-blue-700 text-white rounded-none border-2 border-black dark:border-white shadow-[2px_2px_0px_#000000] dark:shadow-[2px_2px_0px_#FFFFFF]" onClick={() => setActiveTab('maintenance')}>
                  Open Maintenance Suite
                </Button>
              </div>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'maintenance' && (isSuperAdmin || isOperationsAdmin) && (
        <AdminMaintenanceManager />
      )}

      {activeTab === 'config' && isSuperAdmin && (
        <div className="space-y-6">
          <Card className="p-6 bg-white dark:bg-slate-900 rounded-none">
            <h3 className="font-bold mb-4"><Settings size={18} className="inline mr-2"/> Platform Configuration Overrides</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 border-2 border-black dark:border-white rounded-none hover:bg-white dark:hover:bg-slate-800 transition">
                <div>
                  <div className="font-semibold text-sm">Guided Setup Template Editor</div>
                  <div className="text-xs text-gray-500">Modify global onboarding wizards (Youth Sports, HOA, Union).</div>
                </div>
                <Button variant="secondary" size="sm" onClick={() => navigate('/system-admin/templates')}>Edit Templates</Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'newsroom' && (isSuperAdmin || isSupportAdmin) && (
        <AdminNewsroomManager />
      )}

      {activeTab === 'tickets' && (isSuperAdmin || isSupportAdmin) && (
        <AdminTicketsManager />
      )}

      {activeTab === 'audit' && (isSuperAdmin || isSupportAdmin) && (
        <AdminAuditManager />
      )}

      {activeTab === 'nodes' && (isSuperAdmin || isOperationsAdmin) && (
        <AdminNodesManager />
      )}

      {activeTab === 'users' && (isSuperAdmin || isSupportAdmin || isOperationsAdmin) && (
        <AdminUsersManager />
      )}

      {activeTab === 'inquiries' && (isSuperAdmin || isSupportAdmin || isOperationsAdmin) && (
        <AdminContactInquiries />
      )}

      {activeTab === 'moderation' && (isSuperAdmin || isSupportAdmin || isSystemAdmin) && (
        <AdminModerationManager />
      )}

      <AdminBroadcastModal 
        isOpen={broadcastOpen} 
        onClose={() => setBroadcastOpen(false)} 
        defaultNodeId="platform-root"
        defaultNodeName="Platform-Wide Broadcast"
      />
    </div>
  );
};
